import React, {
 createContext,
 useContext,
 useEffect,
 useRef,
 useState,
} from "react";
import { useNavigate } from "react-router-dom";
// import { toast } from "react-toastify"; // optional if you use toast

const AutoLogoutContext = createContext();

export const useAutoLogout = () => {
 const context = useContext(AutoLogoutContext);
 if (!context) {
  throw new Error("useAutoLogout must be used within an AutoLogoutProvider");
 }
 return context;
};

export const AutoLogoutProvider = ({ children }) => {
 // read from localStorage once into a ref
 const savedSettingsRef = useRef(
  JSON.parse(localStorage.getItem("autoLogoutSettings") || "null")
 );

 // initialise state directly from the ref (no useEffect needed)
 const [settings, setSettings] = useState(() => {
  return (
   savedSettingsRef.current || {
    neverLogout: true,
    logoutTimeMinutes: 0,
   }
  );
 });

 const [showWarning, setShowWarning] = useState(false);
 const [warningCountdown, setWarningCountdown] = useState(30);

 const lastActivityRef = useRef(Date.now());
 const intervalRef = useRef(null);
 const warningIntervalRef = useRef(null);
 const navigate = useNavigate();

 // Function to refresh settings from localStorage
 const refreshSettingsFromStorage = () => {
  const storedSettings = JSON.parse(localStorage.getItem("autoLogoutSettings") || "null");
  if (storedSettings) {
   setSettings(storedSettings);
   savedSettingsRef.current = storedSettings;
  }
 };

 // Listen for localStorage changes from other tabs/windows
 useEffect(() => {
  const handleStorageChange = (e) => {
   if (e.key === "autoLogoutSettings" && e.newValue) {
    const newSettings = JSON.parse(e.newValue);
    setSettings(newSettings);
    savedSettingsRef.current = newSettings;
    
    // Reset warning state when settings change
    setShowWarning(false);
    setWarningCountdown(30);
    lastActivityRef.current = Date.now();
   }
  };

  window.addEventListener('storage', handleStorageChange);
  
  return () => {
   window.removeEventListener('storage', handleStorageChange);
  };
 }, []);

 // Listen for custom event for same-tab localStorage updates
 useEffect(() => {
  const handleAutoLogoutSettingsUpdate = () => {
   refreshSettingsFromStorage();
   // Reset warning state when settings change
   setShowWarning(false);
   setWarningCountdown(30);
   lastActivityRef.current = Date.now();
  };

  window.addEventListener('autoLogoutSettingsUpdated', handleAutoLogoutSettingsUpdate);
  
  return () => {
   window.removeEventListener('autoLogoutSettingsUpdated', handleAutoLogoutSettingsUpdate);
  };
 }, []);

 // keep ref & localStorage in sync whenever settings change (only for internal updates)
 useEffect(() => {
  savedSettingsRef.current = settings;
  localStorage.setItem("autoLogoutSettings", JSON.stringify(settings));
 }, [settings]);

 // Handle logout
 const handleLogout = () => {
  setShowWarning(false);
  if (intervalRef.current) clearInterval(intervalRef.current);
  if (warningIntervalRef.current) clearInterval(warningIntervalRef.current);

  localStorage.removeItem("bss_authuser");
  localStorage.removeItem("permissions");
  localStorage.removeItem("bss_authtoken");
  localStorage.removeItem("autoLogoutSettings");

  // toast.info("You have been logged out due to inactivity", {
  // position: toast.POSITION.TOP_CENTER,
  // });

  navigate("/login");
 };

 // Extend session (reset activity)
 const extendSession = () => {
  lastActivityRef.current = Date.now();
  setShowWarning(false);
  setWarningCountdown(30);

  // Clear warning interval when extending session
  if (warningIntervalRef.current) {
   clearInterval(warningIntervalRef.current);
   warningIntervalRef.current = null;
  }
 };

 // Reset last activity on user events (only when warning is not shown)
 const activityEvents = [
  "mousedown",
  "mousemove",
  "keypress",
  "scroll",
  "touchstart",
  "click",
 ];

 useEffect(() => {
  const handleActivity = (e) => {
   // Prevent activity tracking when warning popup is shown
   if (showWarning) {
    // Allow clicks only on the warning modal buttons
    const isWarningModal = e.target.closest('.auto-logout-warning-modal');
    if (!isWarningModal) {
     e.preventDefault();
     e.stopPropagation();
     return false;
    }
    return;
   }

   lastActivityRef.current = Date.now();
  };

  activityEvents.forEach((event) => {
   document.addEventListener(event, handleActivity, true);
  });

  return () => {
   activityEvents.forEach((event) => {
    document.removeEventListener(event, handleActivity, true);
   });
  };
 }, [showWarning]);

 // Interval check for main timeout
 useEffect(() => {
  if (settings.neverLogout || settings.logoutTimeMinutes <= 0) {
   if (intervalRef.current) clearInterval(intervalRef.current);
   if (warningIntervalRef.current) clearInterval(warningIntervalRef.current);
   return;
  }

  const totalMs = settings.logoutTimeMinutes * 60 * 1000;

  intervalRef.current = setInterval(() => {
   const now = Date.now();
   const inactiveTime = now - lastActivityRef.current;
   const remainingMs = totalMs - inactiveTime;

   // Show warning 30 seconds before logout
   if (remainingMs <= 30000 && remainingMs > 0 && !showWarning) {
    setShowWarning(true);
    const initialCountdown = Math.ceil(remainingMs / 1000);
    setWarningCountdown(initialCountdown);
   }

   if (remainingMs <= 0) {
    handleLogout();
   }
  }, 1000);

  return () => {
   if (intervalRef.current) clearInterval(intervalRef.current);
   if (warningIntervalRef.current) clearInterval(warningIntervalRef.current);
  };
 }, [settings, showWarning]);

 // Separate effect for warning countdown
 useEffect(() => {
  if (showWarning) {
   warningIntervalRef.current = setInterval(() => {
    setWarningCountdown((prev) => {
     if (prev <= 1) {
      handleLogout();
      return 0;
     }
     return prev - 1;
    });
   }, 1000);
  } else {
   if (warningIntervalRef.current) {
    clearInterval(warningIntervalRef.current);
    warningIntervalRef.current = null;
   }
  }

  return () => {
   if (warningIntervalRef.current) {
    clearInterval(warningIntervalRef.current);
    warningIntervalRef.current = null;
   }
  };
 }, [showWarning]);

 // Block all interactions when warning is shown
 useEffect(() => {
  if (showWarning) {
   // Add styles to make overlay non-interactive except for modal
   document.body.style.overflow = 'hidden';

   // Prevent keyboard events
   const handleKeyDown = (e) => {
    // Check if target is an Element and has closest method
    let isWarningModal = false;
    if (e.target && typeof e.target.closest === 'function') {
     isWarningModal = e.target.closest('.auto-logout-warning-modal');
    } else if (e.target && e.target.parentElement) {
     // Fallback for targets without closest method
     let element = e.target.parentElement;
     while (element) {
      if (element.classList && element.classList.contains('auto-logout-warning-modal')) {
       isWarningModal = true;
       break;
      }
      element = element.parentElement;
     }
    }
    
    if (!isWarningModal) {
     e.preventDefault();
     e.stopPropagation();
    }
   };

   document.addEventListener('keydown', handleKeyDown, true);

   return () => {
    document.body.style.overflow = 'unset';
    document.removeEventListener('keydown', handleKeyDown, true);
   };
  }
 }, [showWarning]);

 console.log(settings, "settingssettings");

 return (
  <AutoLogoutContext.Provider
   value={{
    settings,
    setSettings,
    extendSession,
    showWarning,
    warningCountdown,
    refreshSettingsFromStorage, // Expose this function for external use
   }}
  >
   {children}

   {showWarning && (
    <div
     className="auto-logout-warning-overlay"
     style={{
      position: 'fixed',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      backgroundColor: 'rgba(0, 0, 0, 0.7)',
      zIndex: 9999,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
     }}
    >
     <div
      className="auto-logout-warning-modal"
      style={{
       backgroundColor: 'white',
       borderRadius: '8px',
       maxWidth: '500px',
       width: '90%',
       boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
       position: 'relative',
       zIndex: 100000,
       pointerEvents: 'auto',
       userSelect: 'auto'
      }}
      onScroll={(e) => e.stopPropagation()}
      onWheel={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
     >
      <div className="modal-header" style={{borderBottom: '1px solid #eee', padding: '20px' }}>
       <h4 className="modal-title" style={{ margin: 0, color: '#333' }}>
        <i className="fas fa-exclamation-triangle text-warning me-2"></i>
        Session Timeout Warning
       </h4>
      </div>
      <div className="modal-body text-center" >
       <h4 style={{ lineHeight: '1.5' }}>
        Your session will expire in <span id="countdown" className="countdown-display" style={{
          color: '#d9534f',
          fontWeight: 'bold',
         }}
        >
         {warningCountdown} sec
        </span> due to inactivity. Please click continue to extend the session
        or log out.
       </h4>
      </div>
      <div className="modal-footer" style={{ display: 'flex', justifyContent: 'center', gap: '10px' , padding: '20px'}}>
       <button
        type="button"
        className="btn btn-secondary me-2"
        onClick={handleLogout}
        style={{
         padding: '10px 20px',
         border: 'none',
         borderRadius: '5px',
         backgroundColor: '#6c757d',
         color: 'white',
         cursor: 'pointer'
        }}
       >
        Logout Now
       </button>
       <button
        type="button"
        className="btn btn-primary"
        onClick={extendSession}
        style={{
         padding: '10px 20px',
         border: 'none',
         borderRadius: '5px',
         backgroundColor: '#007bff',
         color: 'white',
         cursor: 'pointer'
        }}
       >
        Stay Logged In
       </button>
      </div>
     </div>
    </div>
   )}
  </AutoLogoutContext.Provider>
 );
};